import { ScrollArea } from '@radix-ui/themes';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertPopover from 'components/HubertUI/HubertPopover';
import HubertPopoverFieldEdit from 'components/HubertUI/HubertPopoverFieldEdit';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import MutedText from 'components/common/MutedText';
import { CircleMinus, MessagesSquare } from 'lucide-react';
import { toast } from 'sonner';
import { EUtter, stripHTML } from '@hubertai/screening-question-types';

import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { updateSchedulingReplies } from 'rtk/Question.Editor.Slice';
import { selectQuestionLanguage, selectQuestionReplies } from 'rtk/questionEditorSelector';
import { useQuestionEditorContext } from '../context/create-question.context';

/**
 * Booking-confirmed reply line(s) for the scheduling (cal.com) question — what Hubert says in the
 * conversation once a time is booked, per language. Mirrors ReplyPopover's UI (that component is
 * hard-wired to the option/intent reducers and their indices), but the QUESTION-level replies here
 * are a plain per-language string[] behind one reducer, so add/edit/remove all submit the new array.
 */
const SchedulingRepliesPopover = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const repliesByLanguage = useAppSelector((state) => selectQuestionReplies(state, questionId));
  const mainContent = document.getElementById('main-content') as HTMLElement;

  const replies = repliesByLanguage?.[questionLanguage] ?? [];

  const commit = (next: string[]) => {
    dispatch(updateSchedulingReplies({ questionId, language: questionLanguage, replies: next }));
  };

  // Same rich-text hygiene as ReplyPopover's Reply: trim the editor's trailing empty paragraph and
  // unwrap utter_* sentinels a paste may have wrapped in markup.
  const sanitize = (value: string): string => {
    let s = value;
    if (s.endsWith('<p><br></p>')) {
      s = s.substring(0, s.length - 11);
    }
    if (Object.values(EUtter).includes(stripHTML(s)?.trim() as EUtter)) {
      s = stripHTML(s)?.trim() || '';
    }
    if (!stripHTML(s)?.replace(/\s/g, '').length) {
      toast.info(t('reply.toast.emptyMessages'));
    }
    return s;
  };

  return (
    <HubertPopover>
      <HubertTooltip>
        <HubertTooltip.Trigger asChild>
          <HubertPopover.Trigger asChild>
            <HubertButton
              variant="secondary"
              className="cursor-pointer"
              icon={<MessagesSquare className={replies.length ? 'text-primary-500' : 'text-gray-500'} />}
            ></HubertButton>
          </HubertPopover.Trigger>
        </HubertTooltip.Trigger>
        <HubertTooltip.Content>{t('replyPopover.tooltip')}</HubertTooltip.Content>
      </HubertTooltip>
      <HubertPopover.Content
        className={clsx('flex flex-col justify-between', 'min-h-[100px]', 'max-w-[500px] min-w-[500px]', 'p-[18px]')}
        portalContent={true}
        portalElement={mainContent}
      >
        <div className={clsx('mr-4 flex flex-row justify-between', 'pb-2')}>
          <span>
            {t('replyPopover.heading')}
            <MutedText className="pl-2 text-sm font-normal text-gray-500">
              {t('createScheduling.repliesSubheading')}
            </MutedText>
          </span>

          <HubertButton size="xs" onClick={() => commit([...replies, ''])}>
            {t('replyPopover.addReply')}
          </HubertButton>
        </div>

        <ScrollArea className={clsx('max-h-[200px] min-h-[200px]', 'my-4')}>
          {replies.map((reply, index) => (
            <div
              key={`scheduling-reply-${index}`}
              className={clsx(
                'flex flex-row justify-between gap-4',
                'hubert-vertical-center',
                'px-4 py-4',
                'hover:bg-neutral-purple',
                'rounded-md',
              )}
            >
              <HubertPopoverFieldEdit>
                <HubertPopoverFieldEdit.Trigger
                  tooltipContent={t('reply.editLabelTooltip')}
                  containerClass="w-full"
                  asChild
                >
                  <div
                    className={clsx(
                      'text-sm',
                      'text-wrap',
                      'cursor-pointer',
                      'border border-gray-200',
                      'p-3',
                      'rounded-md',
                    )}
                    dangerouslySetInnerHTML={{
                      __html: reply && stripHTML(reply)?.replace(/\s/g, '').length ? reply : t('reply.clickToEdit'),
                    }}
                  />
                </HubertPopoverFieldEdit.Trigger>
                <HubertPopoverFieldEdit.Content
                  editType="rich-text"
                  value={reply}
                  onFieldCommit={(value) => {
                    commit(replies.map((r, i) => (i === index ? sanitize(value) : r)));
                  }}
                >
                  <div>
                    <div className="text-lg font-semibold">{t('reply.editHeading')}</div>
                  </div>
                </HubertPopoverFieldEdit.Content>
              </HubertPopoverFieldEdit>

              <div className={clsx('flex flex-row justify-between gap-4')}>
                <CircleMinus onClick={() => commit(replies.filter((_, i) => i !== index))} />
              </div>
            </div>
          ))}
        </ScrollArea>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default SchedulingRepliesPopover;
